import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from './api';
import { moaniPriceService } from './services/MoaniPriceService';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authCheckRef = useRef(0);

  // Check if user is already authenticated on load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const checkId = ++authCheckRef.current;
    try {
      setLoading(true);
      const response = await api.get('/auth/session', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      if (checkId !== authCheckRef.current) return;
      if (response.data.authenticated) {
        setUser(response.data.user);
        moaniPriceService.getPrice().catch(console.warn);
      } else {
        setUser(null);
      }
      setError(null);
    } catch (err) {
      if (checkId !== authCheckRef.current) return;
      setUser(null);
      console.error('Auth check failed:', err);
    } finally {
      if (checkId === authCheckRef.current) {
        setLoading(false);
      }
    }
  };

  const login = async (username, password) => {
    authCheckRef.current += 1;
    try {
      setLoading(true);
      const response = await api.post('/auth/login', { 
        username, 
        password 
      });
      
      if (response.data.success) {
        setUser(response.data.user);
        setError(null);
        moaniPriceService.getPrice().catch(console.warn);
        return true;
      } else {
        setError('Login failed');
        return false;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await api.post('/auth/logout');
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        loading, 
        error, 
        login, 
        logout, 
        isAuthenticated: !!user 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
} 